import React, { useState, useMemo } from 'react';
import '../styles/Unclaimeddeposite.css';

// ── DATA ──────────────────────────────────────────────────────
// Your real data — 45,354 records from Deaf_List_For_Website__1_.xlsx
import UNCLAIMED_DATA from '../pages/Unclaimeddata';

// ── CONSTANTS ─────────────────────────────────────────────────
const PAGE_SIZE = 10;

const BADGE_CLASS = {
  Savings: 'ud-badge-savings',
  Current: 'ud-badge-current',
  FD:      'ud-badge-fd',
  RD:      'ud-badge-rd',
};

// ── ICONS ─────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
const NoResultIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);
const InitialIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
  </svg>
);

// ── COMPONENT ─────────────────────────────────────────────────
export default function UnclaimedDeposits() {
  const [tab, setTab]           = useState('name');
  const [query, setQuery]       = useState('');
  const [searched, setSearched] = useState(false);
  const [page, setPage]         = useState(1);

  const placeholders = {
    name:    'Enter account holder name...',
    account: 'Enter account number...',
  };

  // ── Filter ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!searched || !query.trim()) return [];
    const q = query.trim().toLowerCase();
    return UNCLAIMED_DATA.filter(row =>
      tab === 'name'
        ? row.name.toLowerCase().includes(q)
        : row.accountNo.toLowerCase().includes(q)
    );
  }, [query, tab, searched]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Handlers ─────────────────────────────────────────────────
  const handleSearch = () => { setSearched(true); setPage(1); };
  const handleClear  = () => { setQuery(''); setSearched(false); setPage(1); };
  const switchTab    = (t) => { setTab(t); setQuery(''); setSearched(false); setPage(1); };

  // Smart pagination: show first, last, and window around current page
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
    .reduce((acc, n, idx, arr) => {
      if (idx > 0 && n - arr[idx - 1] > 1) acc.push('...');
      acc.push(n);
      return acc;
    }, []);

  return (
    <div className="ud-page">

      {/* ── HERO ── */}
      <section className="ud-hero">
        <div className="ud-hero-inner">
          <span className="ud-hero-tag">RBI Mandate — Depositor Education &amp; Awareness</span>
          <h2>Unclaimed Deposit Search</h2>
          <p>
            As per the Reserve Bank of India guidelines, we are required to publish a list
            of accounts that have been inactive for 10 years or more. If you believe you
            have an unclaimed deposit with us, search below using your account number or name.
          </p>
          <div className="ud-hero-note">
            All unclaimed deposits are transferred to the Depositor Education and Awareness (DEA)
            Fund maintained by the RBI. Account holders or their legal heirs may still claim
            these amounts by visiting the nearest branch.
          </div>
        </div>
      </section>

      {/* ── SEARCH CARD ── */}
      <section className="ud-search-section">
        <div className="ud-search-card">
          <h3>Search Unclaimed Deposits</h3>
          <p>
            Search across <strong>{UNCLAIMED_DATA.length.toLocaleString()}</strong> records
            by account holder name or account number.
          </p>

          <div className="ud-tabs">
            <button
              className={`ud-tab ${tab === 'name' ? 'active' : ''}`}
              onClick={() => switchTab('name')}
            >
              🔍 Search by Name
            </button>
            <button
              className={`ud-tab ${tab === 'account' ? 'active' : ''}`}
              onClick={() => switchTab('account')}
            >
              🔢 Search by Account Number
            </button>
          </div>

          <div className="ud-search-row">
            <div className="ud-input-wrap">
              <SearchIcon />
              <input
                type="text"
                value={query}
                placeholder={placeholders[tab]}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                autoComplete="off"
                inputMode={tab === 'account' ? 'numeric' : 'text'}
              />
            </div>
            <button className="ud-btn-search" onClick={handleSearch}>Search</button>
            <button className="ud-btn-clear"  onClick={handleClear}>Clear</button>
          </div>
        </div>
      </section>

      {/* ── RESULTS ── */}
      <section className="ud-results-section">

        {!searched && (
          <div className="ud-state-box">
            <div className="ud-state-icon"><InitialIcon /></div>
            <h4>Enter details to search</h4>
            <p>Use the search box above to look up unclaimed deposits by account holder name or account number across all branches.</p>
          </div>
        )}

        {searched && filtered.length === 0 && (
          <div className="ud-state-box">
            <div className="ud-state-icon"><NoResultIcon /></div>
            <h4>No records found</h4>
            <p>
              No unclaimed deposits were found matching your search. Please check the
              spelling or try searching by account number. For further assistance, visit
              your nearest Sahyadri Sahakari Bank branch.
            </p>
          </div>
        )}

        {searched && filtered.length > 0 && (
          <>
            <div className="ud-disclaimer">
              <strong>Important Notice:</strong> To claim your deposit, please visit your nearest
              Sahyadri Sahakari Bank branch with valid KYC documents and proof of account ownership.
              For deceased account holders, legal heir certificates and succession documents are required.
            </div>

            <div className="ud-results-meta">
              <span>Found <strong>{filtered.length.toLocaleString()}</strong> record(s) &nbsp;·&nbsp; Page {page} of {totalPages}</span>
              <span className="ud-date-note">Data as of: <strong>31 March 2024</strong></span>
            </div>

            <div className="ud-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="ud-col-sr">#</th>
                    <th>Account No.</th>
                    <th>Account Holder Name</th>
                    <th>Account Type</th>
                    <th>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((row, i) => (
                    <tr key={i}>
                      <td className="ud-col-sr ud-sr-no">{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="ud-acc-no">{row.accountNo}</td>
                      <td className="ud-name">{row.name}</td>
                      <td>
                        <span className={`ud-badge ${BADGE_CLASS[row.type] || 'ud-badge-savings'}`}>
                          {row.type}
                        </span>
                      </td>
                      <td className="ud-branch">{row.branch}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="ud-pagination">
                <button className="ud-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  &#8592; Prev
                </button>
                {pageNumbers.map((n, i) =>
                  n === '...'
                    ? <span key={`d${i}`} className="ud-page-dots">…</span>
                    : <button
                        key={n}
                        className={`ud-page-btn ${n === page ? 'active' : ''}`}
                        onClick={() => setPage(n)}
                      >{n}</button>
                )}
                <button className="ud-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next &#8594;
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── CONTACT STRIP ── */}
      <div className="ud-contact-strip">
        <div className="ud-contact-inner">
          <div className="ud-contact-text">
            <h4>Need help claiming your deposit?</h4>
            <p>Visit your nearest branch with valid ID proof and account details.</p>
          </div>
          <div className="ud-contact-links">
            <a href="tel:02222001234"              className="ud-contact-link">📞 022-22017477</a>
            <a href="mailto:info@sahyadribank.com" className="ud-contact-link">✉ customersupport@thesahyadribank.com</a>
            <a href="/branches"                    className="ud-contact-link primary">Find Branch</a>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="ud-footer">
        <strong>The Sahyadri Sahakari Bank Ltd., Mumbai</strong><br />
        Regulated by the Reserve Bank of India &nbsp;·&nbsp; Member, DICGC<br />
        This portal is updated annually as per RBI Circular DBOD.No.DEAF Cell.BC<br />
        &copy; {new Date().getFullYear()} Sahyadri Sahakari Bank. All rights reserved.
      </footer>
    </div>
  );
}